import {VoiceLanguage} from '@Panels/ai-voice-panel/ai-voice-panel.types';

// Invariant: these must equal the charged price in app/Libraries/Facades/ — PollyFacade::CREDITS_TO_CHARGE_GENERATIVE / ::CREDITS_TO_CHARGE_NEURAL and OpenAIApiFacade::TEXT_TO_SPEECH_CREDITS_TO_CHARGE.
export const VOICE_CREDITS_GENERATIVE = 2;
const VOICE_CREDITS_NEURAL = 1;
const VOICE_CREDITS_GPT = 1;

export interface RosterVoiceConfig {
  id: number;
  name: string;
  descriptionKey: string;
  accentKey?: string;
  avatarFile: string;
  credits: number;
  /** Kept out of the dropdown, but still resolvable for persisted/historical picks. */
  isHiddenFromPicker?: boolean;
}

export interface LanguageRosterConfig {
  flagFile: string;
  nameKey: string;
  defaultVoiceId: number;
  voices: RosterVoiceConfig[];
}

/**
 * Frontend mirror of the backend voice maps: PollyTextToSpeech.php owns ids 100+
 * (blocks of twenty per language: 100-119 English, 120-139 French, 140-159 Spanish,
 * 160-179 German, 180-199 Polish, 200-219 Japanese, 220-239 Arabic, 240-259 Turkish,
 * then the expansion blocks: 260-279 English, 280-299 Italian, 300-319 Portuguese,
 * 320-339 Dutch, 340-359 Korean, 360-379 Mandarin, 380-399 Cantonese, 400-419 Catalan,
 * 420-439 Czech, 440-459 Danish, 460-479 Finnish, 480-499 Norwegian, 500-519 Swedish)
 * and OpenAITextToSpeech.php owns the GPT persona voices at ids 1-13, listed under
 * English. The persisted voice preference alone also encodes the last-used language.
 */
export const LANGUAGE_ROSTER: Record<VoiceLanguage, LanguageRosterConfig> = {
  [VoiceLanguage.ENGLISH]: {
    flagFile: 'en.svg',
    nameKey: 'pmwjs_language_english',
    defaultVoiceId: 4,
    // Order: analytics-informed, manually curated (first slots and accent
    // placement are deliberate product choices, not a strict algorithm).
    voices: [
      {id: 4, name: 'James', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_accent_american', avatarFile: 'james.png', credits: VOICE_CREDITS_GPT},
      {id: 7, name: 'Andrew', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_accent_american', avatarFile: 'andrew.png', credits: VOICE_CREDITS_GPT},
      {id: 1, name: 'Jordan', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_accent_american', avatarFile: 'jordan.png', credits: VOICE_CREDITS_GPT},
      {id: 102, name: 'Amy', descriptionKey: 'pmwjs_calm', accentKey: 'pmwjs_accent_british', avatarFile: 'amy.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 103, name: 'Olivia', descriptionKey: 'pmwjs_engaging', accentKey: 'pmwjs_accent_australian', avatarFile: 'olivia.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 13, name: 'Cole', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_accent_american', avatarFile: 'cole.png', credits: VOICE_CREDITS_GPT},
      {id: 109, name: 'Brian', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_accent_british', avatarFile: 'brian.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 260, name: 'Kajal', descriptionKey: 'pmwjs_warm', accentKey: 'pmwjs_accent_indian', avatarFile: 'kajal.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 100, name: 'Matthew', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_accent_american', avatarFile: 'matthew.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 101, name: 'Ruth', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_accent_american', avatarFile: 'ruth.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 6, name: 'Kristen', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_accent_american', avatarFile: 'kristen.png', credits: VOICE_CREDITS_GPT},
      {id: 261, name: 'Niamh', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_accent_irish', avatarFile: 'niamh.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 5, name: 'Allison', descriptionKey: 'pmwjs_conversational', accentKey: 'pmwjs_accent_american', avatarFile: 'allison.png', credits: VOICE_CREDITS_GPT},
      {id: 104, name: 'Joanna', descriptionKey: 'pmwjs_warm', accentKey: 'pmwjs_accent_american', avatarFile: 'joanna.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 106, name: 'Stephen', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_accent_american', avatarFile: 'stephen.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 2, name: 'Taylor', descriptionKey: 'pmwjs_clear_voice', accentKey: 'pmwjs_accent_american', avatarFile: 'taylor.png', credits: VOICE_CREDITS_GPT},
      {id: 264, name: 'Ayanda', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_accent_south_african', avatarFile: 'ayanda.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 8, name: 'Jodie', descriptionKey: 'pmwjs_upbeat', accentKey: 'pmwjs_accent_american', avatarFile: 'jodie.png', credits: VOICE_CREDITS_GPT},
      {id: 105, name: 'Danielle', descriptionKey: 'pmwjs_conversational', accentKey: 'pmwjs_accent_american', avatarFile: 'danielle.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 3, name: 'Chris', descriptionKey: 'pmwjs_engaging', accentKey: 'pmwjs_accent_british', avatarFile: 'chris.png', credits: VOICE_CREDITS_GPT},
      {id: 107, name: 'Salli', descriptionKey: 'pmwjs_clear_voice', accentKey: 'pmwjs_accent_american', avatarFile: 'salli.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 262, name: 'Aria', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_accent_new_zealand', avatarFile: 'aria.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 108, name: 'Tiffany', descriptionKey: 'pmwjs_upbeat', accentKey: 'pmwjs_accent_american', avatarFile: 'tiffany.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 263, name: 'Jasmine', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_accent_singaporean', avatarFile: 'jasmine.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 12, name: 'Marina', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_accent_american', avatarFile: 'marina.png', credits: VOICE_CREDITS_GPT},
      {id: 9, name: 'Eleanor', descriptionKey: 'pmwjs_calm', accentKey: 'pmwjs_accent_american', avatarFile: 'eleanor.png', credits: VOICE_CREDITS_GPT},
      {id: 10, name: 'Elliot', descriptionKey: 'pmwjs_expressive', accentKey: 'pmwjs_accent_british', avatarFile: 'elliot.png', credits: VOICE_CREDITS_GPT},
      {id: 11, name: 'Miles', descriptionKey: 'pmwjs_energetic', accentKey: 'pmwjs_accent_american', avatarFile: 'miles.png', credits: VOICE_CREDITS_GPT},
      {
        id: 110,
        name: 'Emma',
        descriptionKey: 'pmwjs_expressive',
        accentKey: 'pmwjs_accent_british',
        avatarFile: 'emma.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
      {
        id: 111,
        name: 'Arthur',
        descriptionKey: 'pmwjs_engaging',
        accentKey: 'pmwjs_accent_british',
        avatarFile: 'arthur.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
      {
        id: 112,
        name: 'Gregory',
        descriptionKey: 'pmwjs_formal',
        accentKey: 'pmwjs_accent_american',
        avatarFile: 'gregory.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
      {
        id: 113,
        name: 'Kendra',
        descriptionKey: 'pmwjs_calm',
        accentKey: 'pmwjs_accent_american',
        avatarFile: 'kendra.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
      {
        id: 114,
        name: 'Kimberly',
        descriptionKey: 'pmwjs_upbeat',
        accentKey: 'pmwjs_accent_american',
        avatarFile: 'kimberly.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
      {id: 115, name: 'Joey', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_accent_american', avatarFile: 'joey.png', credits: VOICE_CREDITS_NEURAL, isHiddenFromPicker: true},
      {id: 116, name: 'Ivy', descriptionKey: 'pmwjs_energetic', accentKey: 'pmwjs_accent_american', avatarFile: 'ivy.png', credits: VOICE_CREDITS_NEURAL, isHiddenFromPicker: true},
      {
        id: 117,
        name: 'Justin',
        descriptionKey: 'pmwjs_energetic',
        accentKey: 'pmwjs_accent_american',
        avatarFile: 'justin.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
      {
        id: 118,
        name: 'Kevin',
        descriptionKey: 'pmwjs_casual',
        accentKey: 'pmwjs_accent_american',
        avatarFile: 'kevin.png',
        credits: VOICE_CREDITS_NEURAL,
        isHiddenFromPicker: true,
      },
    ],
  },
  [VoiceLanguage.FRENCH]: {
    flagFile: 'fr.svg',
    nameKey: 'pmwjs_language_french',
    defaultVoiceId: 124,
    voices: [
      {id: 124, name: 'Florian', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_language_french', avatarFile: 'florian.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 120, name: 'Léa', descriptionKey: 'pmwjs_warm', accentKey: 'pmwjs_language_french', avatarFile: 'lea.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 121, name: 'Rémi', descriptionKey: 'pmwjs_conversational', accentKey: 'pmwjs_language_french', avatarFile: 'remi.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 126, name: 'Gabrielle', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_accent_canadian', avatarFile: 'gabrielle.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 125, name: 'Isabelle', descriptionKey: 'pmwjs_clear_voice', accentKey: 'pmwjs_accent_belgian', avatarFile: 'isabelle.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 122, name: 'Ambre', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_french', avatarFile: 'ambre.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 123, name: 'Céline', descriptionKey: 'pmwjs_calm', accentKey: 'pmwjs_language_french', avatarFile: 'celine.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 127, name: 'Liam', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_accent_canadian', avatarFile: 'liam.png', credits: VOICE_CREDITS_GENERATIVE},
    ],
  },
  [VoiceLanguage.SPANISH]: {
    flagFile: 'es.svg',
    nameKey: 'pmwjs_language_spanish',
    defaultVoiceId: 140,
    voices: [
      {id: 140, name: 'Lucia', descriptionKey: 'pmwjs_upbeat', accentKey: 'pmwjs_language_spanish', avatarFile: 'lucia.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 141, name: 'Sergio', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_language_spanish', avatarFile: 'sergio.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 142, name: 'Mía', descriptionKey: 'pmwjs_conversational', accentKey: 'pmwjs_accent_mexican', avatarFile: 'mia.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 144, name: 'Lupe', descriptionKey: 'pmwjs_upbeat', accentKey: 'pmwjs_accent_american', avatarFile: 'lupe.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 143, name: 'Andrés', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_accent_mexican', avatarFile: 'andres.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 145, name: 'Pedro', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_accent_american', avatarFile: 'pedro.png', credits: VOICE_CREDITS_GENERATIVE},
    ],
  },
  [VoiceLanguage.GERMAN]: {
    flagFile: 'de.svg',
    nameKey: 'pmwjs_language_german',
    defaultVoiceId: 160,
    voices: [
      {id: 160, name: 'Vicki', descriptionKey: 'pmwjs_clear_voice', accentKey: 'pmwjs_language_german', avatarFile: 'vicki.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 161, name: 'Daniel', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_language_german', avatarFile: 'daniel.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 162, name: 'Lennart', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_language_german', avatarFile: 'lennart.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 163, name: 'Hannah', descriptionKey: 'pmwjs_warm', accentKey: 'pmwjs_accent_austrian', avatarFile: 'hannah.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 164, name: 'Sabrina', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_accent_swiss', avatarFile: 'sabrina.png', credits: VOICE_CREDITS_GENERATIVE},
    ],
  },
  [VoiceLanguage.POLISH]: {
    flagFile: 'pl.svg',
    nameKey: 'pmwjs_language_polish',
    defaultVoiceId: 180,
    voices: [
      {id: 180, name: 'Ewa', descriptionKey: 'pmwjs_calm', accentKey: 'pmwjs_language_polish', avatarFile: 'ewa.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 181, name: 'Ola', descriptionKey: 'pmwjs_energetic', accentKey: 'pmwjs_language_polish', avatarFile: 'ola.png', credits: VOICE_CREDITS_GENERATIVE},
    ],
  },
  [VoiceLanguage.JAPANESE]: {
    flagFile: 'ja.svg',
    nameKey: 'pmwjs_language_japanese',
    defaultVoiceId: 200,
    voices: [
      {id: 200, name: 'Kazuha', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_japanese', avatarFile: 'kazuha.png', credits: VOICE_CREDITS_NEURAL},
      {id: 201, name: 'Takumi', descriptionKey: 'pmwjs_conversational', accentKey: 'pmwjs_language_japanese', avatarFile: 'takumi.png', credits: VOICE_CREDITS_NEURAL},
      {id: 202, name: 'Tomoko', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_language_japanese', avatarFile: 'tomoko.png', credits: VOICE_CREDITS_NEURAL},
    ],
  },
  [VoiceLanguage.ARABIC]: {
    flagFile: 'ar.svg',
    nameKey: 'pmwjs_language_arabic',
    defaultVoiceId: 220,
    voices: [
      {id: 220, name: 'Hala', descriptionKey: 'pmwjs_formal', accentKey: 'pmwjs_language_arabic', avatarFile: 'hala.png', credits: VOICE_CREDITS_NEURAL},
      {id: 221, name: 'Zayd', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_language_arabic', avatarFile: 'zayd.png', credits: VOICE_CREDITS_NEURAL},
    ],
  },
  [VoiceLanguage.TURKISH]: {
    flagFile: 'tr.svg',
    nameKey: 'pmwjs_language_turkish',
    defaultVoiceId: 240,
    voices: [{id: 240, name: 'Burcu', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_turkish', avatarFile: 'burcu.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.CANTONESE]: {
    flagFile: 'yue.svg',
    nameKey: 'pmwjs_language_cantonese',
    defaultVoiceId: 380,
    voices: [{id: 380, name: 'Hiujin', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_cantonese', avatarFile: 'hiujin.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.CATALAN]: {
    flagFile: 'ca.svg',
    nameKey: 'pmwjs_language_catalan',
    defaultVoiceId: 400,
    voices: [{id: 400, name: 'Arlet', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_language_catalan', avatarFile: 'arlet.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.CZECH]: {
    flagFile: 'cs.svg',
    nameKey: 'pmwjs_language_czech',
    defaultVoiceId: 420,
    voices: [{id: 420, name: 'Jitka', descriptionKey: 'pmwjs_calm', accentKey: 'pmwjs_language_czech', avatarFile: 'jitka.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.DANISH]: {
    flagFile: 'da.svg',
    nameKey: 'pmwjs_language_danish',
    defaultVoiceId: 440,
    voices: [{id: 440, name: 'Sofie', descriptionKey: 'pmwjs_casual', accentKey: 'pmwjs_language_danish', avatarFile: 'sofie.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.DUTCH]: {
    flagFile: 'nl.svg',
    nameKey: 'pmwjs_language_dutch',
    defaultVoiceId: 320,
    voices: [
      {id: 320, name: 'Laura', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_dutch', avatarFile: 'laura.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 321, name: 'Lisa', descriptionKey: 'pmwjs_warm', accentKey: 'pmwjs_accent_belgian', avatarFile: 'lisa.png', credits: VOICE_CREDITS_GENERATIVE},
    ],
  },
  [VoiceLanguage.FINNISH]: {
    flagFile: 'fi.svg',
    nameKey: 'pmwjs_language_finnish',
    defaultVoiceId: 460,
    voices: [{id: 460, name: 'Suvi', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_finnish', avatarFile: 'suvi.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.ITALIAN]: {
    flagFile: 'it.svg',
    nameKey: 'pmwjs_language_italian',
    defaultVoiceId: 283,
    voices: [
      {id: 283, name: 'Adriano', descriptionKey: 'pmwjs_professional', accentKey: 'pmwjs_language_italian', avatarFile: 'adriano.png', credits: VOICE_CREDITS_NEURAL},
      {id: 280, name: 'Beatrice', descriptionKey: 'pmwjs_energetic', accentKey: 'pmwjs_language_italian', avatarFile: 'beatrice.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 281, name: 'Bianca', descriptionKey: 'pmwjs_expressive', accentKey: 'pmwjs_language_italian', avatarFile: 'bianca.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 282, name: 'Lorenzo', descriptionKey: 'pmwjs_confident', accentKey: 'pmwjs_language_italian', avatarFile: 'lorenzo.png', credits: VOICE_CREDITS_GENERATIVE},
    ],
  },
  [VoiceLanguage.KOREAN]: {
    flagFile: 'ko.svg',
    nameKey: 'pmwjs_language_korean',
    defaultVoiceId: 340,
    voices: [
      {id: 340, name: 'Seoyeon', descriptionKey: 'pmwjs_expressive', accentKey: 'pmwjs_language_korean', avatarFile: 'seoyeon.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 341, name: 'Jihye', descriptionKey: 'pmwjs_warm', accentKey: 'pmwjs_language_korean', avatarFile: 'jihye.png', credits: VOICE_CREDITS_NEURAL},
    ],
  },
  [VoiceLanguage.MANDARIN]: {
    flagFile: 'zh.svg',
    nameKey: 'pmwjs_language_mandarin',
    defaultVoiceId: 360,
    voices: [{id: 360, name: 'Zhiyu', descriptionKey: 'pmwjs_clear_voice', accentKey: 'pmwjs_language_mandarin', avatarFile: 'zhiyu.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.NORWEGIAN]: {
    flagFile: 'no.svg',
    nameKey: 'pmwjs_language_norwegian',
    defaultVoiceId: 480,
    voices: [{id: 480, name: 'Ida', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_norwegian', avatarFile: 'ida.png', credits: VOICE_CREDITS_NEURAL}],
  },
  [VoiceLanguage.PORTUGUESE]: {
    flagFile: 'pt.svg',
    nameKey: 'pmwjs_language_portuguese',
    defaultVoiceId: 300,
    voices: [
      {id: 300, name: 'Camila', descriptionKey: 'pmwjs_conversational', accentKey: 'pmwjs_accent_brazilian', avatarFile: 'camila.png', credits: VOICE_CREDITS_GENERATIVE},
      {id: 301, name: 'Thiago', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_accent_brazilian', avatarFile: 'thiago.png', credits: VOICE_CREDITS_NEURAL},
      {id: 302, name: 'Vitória', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_accent_brazilian', avatarFile: 'vitoria.png', credits: VOICE_CREDITS_NEURAL},
      {id: 303, name: 'Inês', descriptionKey: 'pmwjs_calm', accentKey: 'pmwjs_language_portuguese', avatarFile: 'ines.png', credits: VOICE_CREDITS_NEURAL},
    ],
  },
  [VoiceLanguage.SWEDISH]: {
    flagFile: 'sv.svg',
    nameKey: 'pmwjs_language_swedish',
    defaultVoiceId: 500,
    voices: [{id: 500, name: 'Elin', descriptionKey: 'pmwjs_natural', accentKey: 'pmwjs_language_swedish', avatarFile: 'elin.png', credits: VOICE_CREDITS_NEURAL}],
  },
};
